import React from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import { withTheme } from '@mui/styles';
import variablePlugin from './tinymce.variables.plugin';
import mentionPlugin from './tinymce.mention.plugin';
import FormLabel from '@mui/material/FormLabel';
import { html2text } from 'utils';

export function convertTextToMentionHtml(text) {
  const mentionRegex = /@\[([^\]]+)\]\((\d+)\)/g;
  return text.replace(
    mentionRegex,
    '<span contenteditable="false" class="mce-mention" data-mention-id="$2">@$1</span>',
  );
}

export function htmlToText(txt) {
  const node = document.createElement('div');
  node.innerHTML = txt;

  function processNode(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.dataset.mentionId) {
        node.textContent = `@[${node.textContent.substring(1)}](${
          node.dataset.mentionId
        })`;
      } else {
        node.childNodes.forEach((child) => processNode(child));
      }
    }
  }

  processNode(node);
  return html2text(node.innerHTML);
}

class AdvanceTextEditor extends React.Component {
  state = {
    loading: true,
    key: 1,
  };

  componentWillMount() {
    if (this.props.value) {
      this.handleChange(this.toEditorContent(this.props.value));
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.variables !== this.props.variables) {
      this.setState({
        key: this.state.key + 1, // handle variables prop change and reinit editor
      });
    }
    if (prevProps.mentions !== this.props.mentions) {
      this.setState({
        key: this.state.key + 1, // handle mentions prop change and reinit editor
      });
    }
  }

  // text line breaks to be converted to html line breaks
  textToHtml = (text) => {
    return text.replace(/\n/g, '<br />');
  };

  toEditorContent = (value) => {
    const r = convertTextToMentionHtml(this.textToHtml(value));
    return r;
  };

  componentWillReceiveProps(nextProps) {
    const { value } = nextProps;
    if (!value) return;
    if (!this.props.value && value) {
      this.handleChange(this.toEditorContent(value));
    }
  }

  handleChange = (val, editor) => {
    const { onChange } = this.props;
    onChange(val);
  };

  render() {
    const {
      label,
      value,
      onChange,
      onPaste,
      innerRef,
      placeholder,
      hasError,
      helpText,
      theme,
      id = 'mce',
      height,
      variables,
      mentions,
      onKeyDown,
      onBlur,
      onClick,
      disabled,
      ...other
    } = this.props;

    const isDark = theme.palette.mode === 'dark';
    const plugins = ['autoresize'];
    if (variables) plugins.push('variable');
    if (mentions) plugins.push('mention');

    return (
      <FormControl
        margin="normal"
        fullWidth
        error={hasError}
        sx={{
          '& .tox-tinymce': {
            'borderColor': hasError ? 'error.main' : 'grey.300',
            '&.focused': {
              borderColor: hasError ? 'error.main' : 'primary.main',
            },
          },
        }}
        {...other}
      >
        {label && <FormLabel sx={{ mb: 1 }}>{label}</FormLabel>}
        <Editor
          key={this.state.key}
          disabled={this.state.loading || disabled}
          id={id}
          apiKey="v90a1k43iqyfp5qm7pm4vkc89opy0ptw4sc525pshsvqzvjk"
          init={{
            min_height: height,
            autoresize_bottom_margin: 0,
            skin: isDark ? 'oxide-dark' : undefined,
            content_css: isDark ? 'dark' : undefined,
            auto_focus: false,
            menubar: false,
            statusbar: false,
            browser_spellcheck: true,
            relative_urls: false,
            link_assume_external_targets: true,
            remove_script_host: true,
            contextmenu: false,
            paste_as_text: true,
            paste_data_images: false,
            keep_styles: false,
            plugins,
            paste_preprocess: function (plugin, args) {
              // Access the pasted content
              let content = args.content;

              // Modify the content as needed
              // Example: Removing all HTML tags
              content = content.replace(/<\/?[^>]+(>|$)/g, '');
              // Set the modified content back
              args.content = onPaste ? onPaste(content) : content;
            },
            toolbar: false,
            newline_behavior: 'linebreak',
            variable_mapper: variables,
            mention_options: mentions,
            style_formats_autohide: true,
            style_formats: [],
            content_style: `
                p {
                  margin: 0;
                  padding: 0;
                }
                .variable {
                  display: inline-block;
                  cursor: pointer;
                  background-color: #894FE8;
                  color: #FFF;
                  padding: 2px 10px;
                  border-radius: 14px;
                  font-style: normal;
                  font-size: 14px;
                  line-height: 1;
                }
                .mce-mention {
                  color: #038aff;
                  cursor: default;
                }
              `,
            setup: function (editor) {
              editor.editorManager.PluginManager.add(
                'variable',
                variablePlugin,
              );
              editor.editorManager.PluginManager.add('mention', mentionPlugin);

              editor.on('focus', (e) => {
                e.target.editorContainer.classList.toggle('focused');
              });

              editor.on('blur', (e) => {
                e.target.editorContainer.classList.toggle('focused');
              });
            },
            placeholder,
          }}
          onBlur={onBlur}
          onClick={onClick}
          onEditorChange={this.handleChange}
          ref={innerRef}
          value={value}
          onInit={(e, editor) => {
            // editor.on('variableClick', this.onVariableClick);
            this.setState({ loading: false });
          }}
          onKeyDown={onKeyDown}
          tinymceScriptSrc={'/assets/js/tinymce/js/tinymce/tinymce.min.js'}
        />
        {hasError && <FormHelperText>{helpText}</FormHelperText>}
      </FormControl>
    );
  }
}

AdvanceTextEditor.defaultProps = {
  placeholder: 'Enter your message',
  height: 250,
};

AdvanceTextEditor.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  innerRef: PropTypes.object,
  height: PropTypes.number,
  variables: PropTypes.objectOf(PropTypes.string),
  mentions: PropTypes.arrayOf(PropTypes.object),
};

export default withTheme(AdvanceTextEditor);
